import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import moment from 'moment';
import styles from './Resequence.module.css';
import { API_RESOURCE_URLS, DATE_FORMAT } from '../../constants';

import {
  buildErrorMessage,
  fetchSequenceForDatePlantAndLine,
} from '../../apis/calls';
import {
  processSequenceData,
  formatDataToIncludeColour,
} from '../../utils/tableUtils';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';
import {
  ResequenceFinalPreview,
  ResequenceTransferList,
} from '../../components';
import { API } from '../../apis/api';

const Resequence = () => {
  const { ISODate } = useParams();
  const history = useHistory();
  const { showInternalError } = usePopupManager();

  const [sequenceDraft, setSequenceDraft] = useState(null);
  const [transferListDraft, setTransferListDraft] = useState(null);

  const [sourceSequenceData, setSourceSequenceData] = useState({
    loading: false,
    data: [],
  });

  const [plant] = usePlantAndLinesStore().plantState;
  const [line] = usePlantAndLinesStore().lineState;

  const loadSequenceDataForDatePlantAndLine = async (date, plant, line) => {
    const dateString = date.format(DATE_FORMAT.ISO);
    try {
      setSourceSequenceData(prevState => ({ ...prevState, loading: true }));
      const response = await fetchSequenceForDatePlantAndLine(
        dateString,
        plant,
        line,
      );
      const data = formatDataToIncludeColour(response.data);
      const processedRows = processSequenceData(data);
      setSourceSequenceData({
        data: processedRows,
        loading: false,
      });
    } catch (err) {
      console.error('failed to load sequence err:' + buildErrorMessage(err));
      setSourceSequenceData({
        data: [],
        loading: false,
      });
      showInternalError();
    }
  };

  const currentDate = useMemo(() => moment(ISODate, DATE_FORMAT.ISO), [
    ISODate,
  ]);

  useEffect(() => {
    if (ISODate && plant?.value && line?.value) {
      const date = moment(ISODate, DATE_FORMAT.ISO);
      loadSequenceDataForDatePlantAndLine(date, plant.value, line.value);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ISODate, line, plant]);

  useEffect(() => {
    setSequenceDraft(transferListDraft);
  }, [transferListDraft]);

  const goBackToTransferList = () => setSequenceDraft(null);
  const redirectBackToPreviews = () => history.push('/sequence-previews');

  const updateSequenceOrder = (sequence, date) => {
    return API.put(API_RESOURCE_URLS.VC_SEQUENCE, {
      plant: plant.value,
      line: line.value,
      date: date.format(DATE_FORMAT.ISO),
      vcSequencePlans: sequence.map((seq, index) => {
        return {
          serialNumber: index + 1,
          vc: seq.vc,
          category: seq.category,
          market: seq.market,
          countryCode: seq.countryCode,
          defence: seq.defence,
          fbv: seq.fbv,
          fbvVendorCode: seq.fbvVendorCode,
          buildCardNumber: seq.buildCardNumber,
          powerSteering: seq.powerSteering,
          colourCode: seq.colourCode,
          evrNumber:seq.evrNumber,
          shiftId: null,
          shiftName: null,
        };
      }),
    });
  };

  const saveSequence = sequence => {
    updateSequenceOrder(sequence, currentDate)
      .then(redirectBackToPreviews)
      .catch(error => {
        console.error('Error saving sequence', error);
        showInternalError();
      });
  };

  return (
    <div className={styles.container}>
      {sequenceDraft?.length ? (
        <ResequenceFinalPreview
          date={currentDate}
          sourceData={sequenceDraft}
          lineName={line.label}
          handleConfirm={saveSequence}
          handleBack={goBackToTransferList}
        />
      ) : (
        <ResequenceTransferList
          date={currentDate}
          transferListDraft={transferListDraft}
          sourceData={sourceSequenceData.data}
          lineName={line.label}
          plantName={plant.label}
          handlePreview={sequence => {
            setTransferListDraft(sequence);
          }}
          handleCancel={redirectBackToPreviews}
        />
      )}
    </div>
  );
};

Resequence.propTypes = {};

export default Resequence;
